import { motion } from "framer-motion";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Clock, Info, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import type { LiveTip } from "@/hooks/useLiveTips";

interface LiveTipCardProps {
  tip: LiveTip;
}

export function LiveTipCard({ tip }: LiveTipCardProps) {
  const [timeRemaining, setTimeRemaining] = useState('');

  // Parse current score
  const currentScore = typeof tip.currentScore === 'string'
    ? JSON.parse(tip.currentScore)
    : tip.currentScore;

  // Calculate countdown
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date().getTime();
      const expiry = new Date(tip.expiresAt).getTime();
      const diff = expiry - now;

      if (diff <= 0) {
        setTimeRemaining('Expired');
        return;
      }

      const minutes = Math.floor(diff / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setTimeRemaining(`${minutes}m ${seconds}s`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [tip.expiresAt]);

  // Confidence color based on percentage
  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 75) return "text-primary bg-primary/10 border-primary/20";
    if (confidence >= 60) return "text-yellow-500 bg-yellow-500/10 border-yellow-500/20";
    return "text-red-500 bg-red-500/10 border-red-500/20";
  };

  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= 75) return "High";
    if (confidence >= 60) return "Medium";
    return "Risky";
  };

  const confidenceLabel = getConfidenceLabel(tip.confidence);
  const confidenceColor = getConfidenceColor(tip.confidence);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      className="h-full"
    >
      <Card className="glass h-full overflow-hidden group relative border-2 border-red-500/20">
        {/* Live pulse animation */}
        <div className="absolute top-3 right-3 z-10">
          <div className="relative">
            <div className="animate-ping absolute h-3 w-3 rounded-full bg-red-500 opacity-75"></div>
            <div className="relative h-3 w-3 rounded-full bg-red-500"></div>
          </div>
        </div>

        <CardHeader className="pb-2 border-b border-white/5 bg-gradient-to-r from-red-500/10 to-orange-500/10">
          <div className="flex items-center justify-between text-xs font-medium uppercase tracking-wider">
            <div className="flex items-center gap-2">
              <Badge className="bg-red-500 text-white font-bold text-xs animate-pulse">
                🔴 LIVE
              </Badge>
              <span className="text-muted-foreground">{tip.league}</span>
            </div>
            {tip.isPremium && (
              <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold text-xs">
                PRO
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {/* Match Info */}
          <div className="mb-4 text-center">
            <div className="text-2xl font-bold text-primary mb-1">
              {currentScore.home} - {currentScore.away}
            </div>
            <div className="text-xs text-muted-foreground flex items-center justify-center gap-2">
              <Clock className="w-3 h-3" />
              <span>{tip.minute}'</span>
            </div>
          </div>

          {/* Teams */}
          <div className="flex justify-between items-center mb-6">
            <div className="text-center flex-1">
              <h3 className="text-lg font-bold text-white mb-1">{tip.homeTeam}</h3>
            </div>
            <div className="px-4 text-xs font-bold text-muted-foreground">VS</div>
            <div className="text-center flex-1">
              <h3 className="text-lg font-bold text-white mb-1">{tip.awayTeam}</h3>
            </div>
          </div>

          <div className="space-y-3">
            {/* Prediction */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/5">
              <span className="text-sm text-muted-foreground">Prediction</span>
              <span className="font-bold text-white">{tip.prediction}</span>
            </div>

            {/* Odds */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/5">
              <span className="text-sm text-muted-foreground">Odds</span>
              <span className="font-bold text-primary text-xl">{tip.odds}</span>
            </div>

            {/* Reasoning */}
            {tip.reasoning && (
              <div className="p-3 rounded-lg bg-primary/5 border border-primary/10">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-muted-foreground leading-relaxed">{tip.reasoning}</p>
                </div>
              </div>
            )}

            {/* Confidence and Expiry */}
            <div className="flex items-center justify-between mt-4">
              <Badge variant="outline" className={`rounded-full px-3 py-1 ${confidenceColor}`}>
                {confidenceLabel === 'High' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                {confidenceLabel === 'Medium' && <Zap className="w-3 h-3 mr-1" />}
                {tip.confidence}% Confidence
              </Badge>

              <Badge
                variant="outline"
                className={`rounded-full px-3 py-1 ${
                  timeRemaining === 'Expired'
                    ? 'text-red-500 bg-red-500/10 border-red-500/20'
                    : 'text-orange-500 bg-orange-500/10 border-orange-500/20'
                }`}
              >
                <Clock className="w-3 h-3 mr-1" />
                {timeRemaining === 'Expired' ? 'Expired' : `⏰ ${timeRemaining}`}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
